import React, { useState } from 'react';
import Image from 'next/image';
import { CtaButton } from '@/components/Cta/Cta';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { AUTH_STEPS } from '../../constants';
import { CognitoPasswordStepProps } from '../types';
import openEyeIcon from '@/public/icons/account/eye_open.svg';
import closedEyeIcon from '@/public/icons/account/eye_closed.svg';
import i18n from '@/settings/i18n';

const t = i18n.init('auth');

const PasswordStep = ({
  setStep,
  data,
  setData,
  onSuccess,
}: CognitoPasswordStepProps) => {
  const { cognitoLogin, loading } = useCustomerContext();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Uniform message — never reveal whether the account or password exists.
  const genericAuthError = t`Email or password is incorrect.`;

  const loginAndCallback = async () => {
    if (data.password === '') {
      setError(t`Password is required.`);

      return;
    }

    try {
      const auth = await cognitoLogin(data.email, data.password);

      if (!auth?.error) {
        onSuccess();
      } else {
        setError(genericAuthError);
      }
    } catch (err) {
      setError(genericAuthError);
    }
  };

  return (
    <React.Fragment>
      <div className="text-sm">
        <div className="mb-1">{data.email}</div>
        <button
          onClick={() => setStep(AUTH_STEPS.EMAIL)}
          className="text-left font-bold text-gray-700 underline underline-offset-1"
        >
          {t`Not your email? Go back.`}
        </button>
      </div>
      <div className="">
        <div className="relative">
          <input
            className="mt-2 h-12 w-full rounded border border-gray-700/10 px-3 text-gray-700 outline-1 outline-gray-700 placeholder:text-gray-700/50"
            placeholder={t`Password`}
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            value={data.password}
            onChange={(e) => {
              setError('');
              setData({ ...data, password: e.target.value });
            }}
          />
          <button
            className="absolute right-3 top-1/2 mt-1 h-5 w-5 -translate-y-1/2"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            <Image
              src={showPassword ? openEyeIcon : closedEyeIcon}
              alt={t`Toggle Password`}
              height={50}
              width={50}
              className="h-full w-full"
            />
          </button>
        </div>
        <CtaButton
          type="button"
          className="mt-2.5 !h-12 w-full !rounded !text-sm"
          onClick={() => loginAndCallback()}
          loading={loading}
        >
          {t`Sign in`}
        </CtaButton>
        {error ? <div className="text-sm text-red">{error}</div> : null}
        <button
          type="button"
          onClick={() => setStep(AUTH_STEPS.FORGOT_PASSWORD)}
          className="mb-4 mt-1 block font-bold text-gray-700 underline underline-offset-1"
        >
          {t`Forgot your password?`}
        </button>
      </div>
    </React.Fragment>
  );
};

export default PasswordStep;
